import { authorizedFetch } from './session';

export interface CosmeticRow {
  id: string;
  name: string;
  category: string;
  unlock_type: 'free' | 'level' | 'purchase';
  unlock_level: number | null;
  price_cents: number | null;
  accent_color: string;
  background_color: string;
}

export interface InventoryEntry {
  cosmetic_id: string;
  equipped: number;
}

export interface CosmeticsResponse {
  catalog: CosmeticRow[];
  inventory: InventoryEntry[];
  level: number;
}

export async function fetchCosmetics(): Promise<CosmeticsResponse> {
  return authorizedFetch<CosmeticsResponse>('/api/cosmetics');
}

export async function equipCosmetic(cosmeticId: string): Promise<void> {
  await authorizedFetch(`/api/cosmetics/${cosmeticId}/equip`, { method: 'POST' });
}

const THEME_STORAGE_KEY = 'rw_equipped_timer_theme';

export function rememberEquippedTheme(cosmetic: CosmeticRow): void {
  localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(cosmetic));
}

export function recalledEquippedTheme(): CosmeticRow | null {
  const raw = localStorage.getItem(THEME_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CosmeticRow;
  } catch {
    return null;
  }
}

/** Applies an equipped timer theme's colours as CSS custom properties on the given element. */
export function applyTimerTheme(el: HTMLElement, cosmetic: CosmeticRow | null): void {
  if (!cosmetic) return;
  el.style.setProperty('--colour-signal', cosmetic.accent_color);
  el.style.setProperty('--colour-signal-bright', cosmetic.accent_color);
  el.style.setProperty('--colour-ink-950', cosmetic.background_color);
}

/** Applies the locally-cached equipped theme immediately, then refreshes the cache from the server. */
export function applyCachedTimerTheme(el: HTMLElement): void {
  applyTimerTheme(el, recalledEquippedTheme());
  fetchCosmetics()
    .then(({ catalog, inventory }) => {
      const equipped = inventory.find((row) => row.equipped);
      const cosmetic = equipped ? catalog.find((row) => row.id === equipped.cosmetic_id) : undefined;
      if (cosmetic) {
        rememberEquippedTheme(cosmetic);
        applyTimerTheme(el, cosmetic);
      }
    })
    .catch(() => {
      // not signed in or offline; the cached/default theme already applied
    });
}
